const DEV_SUFFIXES = ['localhost', '127.0.0.1'];
const PORTAL_PREFIXES = ['rider', 'merchant', 'customer'];
const SYSTEM_LABELS = new Set(['admin', 'www']);
const CONFIGURED_BASE_DOMAIN = String(process.env.REACT_APP_BASE_DOMAIN || '')
  .trim()
  .toLowerCase()
  .replace(/^https?:\/\//, '')
  .replace(/\/.*$/, '')
  .replace(/:\d+$/, '');
const PATH_PORTALS_ENABLED = String(process.env.REACT_APP_PORTAL_MODE || '').toLowerCase() === 'path';
const HOST_NAMESPACE = String(process.env.REACT_APP_HOST_NAMESPACE || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9-]/g, '')
  .replace(/^-+|-+$/g, '');
const NAMESPACE_SUFFIX = HOST_NAMESPACE ? `-${HOST_NAMESPACE}` : '';

function isDevHost(hostname = window.location.hostname) {
  const parts = hostname.split('.');
  return DEV_SUFFIXES.includes(parts[parts.length - 1]);
}

export function getBaseDomain(hostname = window.location.hostname) {
  const host = hostname.toLowerCase();
  if (
    CONFIGURED_BASE_DOMAIN
    && (host === CONFIGURED_BASE_DOMAIN || host.endsWith(`.${CONFIGURED_BASE_DOMAIN}`))
  ) {
    return CONFIGURED_BASE_DOMAIN;
  }
  if (isDevHost(hostname)) return 'localhost';
  const parts = hostname.split('.');
  if (parts.length >= 3) return parts.slice(-3).join('.');
  return hostname;
}

function parseFlatPortalLabel(label) {
  for (const prefix of PORTAL_PREFIXES) {
    const flatPrefix = `${prefix}-`;
    if (label.startsWith(flatPrefix)) {
      const slug = label.slice(flatPrefix.length);
      if (slug) return { portal: prefix, companySlug: slug };
    }
  }
  return null;
}

function parseNamespacedLabel(label) {
  if (!HOST_NAMESPACE) return null;
  if (label === HOST_NAMESPACE) return { portal: null, companySlug: null };
  if (!NAMESPACE_SUFFIX || !label.endsWith(NAMESPACE_SUFFIX)) return null;

  const baseLabel = label.slice(0, -NAMESPACE_SUFFIX.length);
  if (!baseLabel) return null;
  if (baseLabel === 'admin') return { portal: 'admin', companySlug: null };

  const flat = parseFlatPortalLabel(baseLabel);
  if (flat) return flat;
  return { portal: 'company', companySlug: baseLabel };
}

function parseLocalhost(hostname) {
  const host = hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') {
    return { portal: null, companySlug: null };
  }
  const suffix = host.endsWith('.localhost') ? '.localhost' : host.endsWith('.127.0.0.1') ? '.127.0.0.1' : null;
  if (!suffix) return null;

  const label = host.slice(0, -suffix.length);
  const parts = label.split('.');

  if (parts.length === 1) {
    const namespaced = parseNamespacedLabel(parts[0]);
    if (namespaced) return namespaced;
    if (parts[0] === 'admin') return { portal: 'admin', companySlug: null };
    const flat = parseFlatPortalLabel(parts[0]);
    if (flat) return flat;
    if (SYSTEM_LABELS.has(parts[0])) return null;
    return { portal: 'company', companySlug: parts[0] };
  }

  if (parts.length === 2 && PORTAL_PREFIXES.includes(parts[0])) {
    return { portal: parts[0], companySlug: parts[1] };
  }

  return null;
}

function parseDefaultDomain(hostname) {
  const baseDomain = getBaseDomain(hostname);
  if (isDevHost(hostname)) return null;

  const host = hostname.toLowerCase();
  const baseParts = baseDomain.split('.');
  const hostParts = host.split('.');
  if (hostParts.slice(-baseParts.length).join('.') !== baseDomain) return null;

  const prefixParts = hostParts.slice(0, -baseParts.length);
  if (prefixParts.length === 0) return { portal: null, companySlug: null };

  if (prefixParts.length === 1) {
    const label = prefixParts[0];
    const namespaced = parseNamespacedLabel(label);
    if (namespaced) return namespaced;
    if (label === 'admin') return { portal: 'admin', companySlug: null };
    if (SYSTEM_LABELS.has(label)) return null;
    const flat = parseFlatPortalLabel(label);
    if (flat) return flat;
    return { portal: 'company', companySlug: label };
  }

  if (prefixParts.length === 2 && PORTAL_PREFIXES.includes(prefixParts[0])) {
    return { portal: prefixParts[0], companySlug: prefixParts[1] };
  }

  return null;
}

/** Full host context: portal + company slug */
export function parseHostContext(hostname = window.location.hostname) {
  const local = parseLocalhost(hostname);
  if (local) return local;

  const parsed = parseDefaultDomain(hostname);
  if (parsed) return parsed;

  return { portal: null, companySlug: null };
}

export function getPortal() {
  return parseHostContext().portal;
}

export function getCompanySlug() {
  return parseHostContext().companySlug;
}

/** Returns 'admin' or company slug — backward compatible */
export function detectSubdomain() {
  const ctx = parseHostContext();
  if (ctx.portal === 'admin') return 'admin';
  return ctx.companySlug;
}

export function isSubdomainEnvironment() {
  return parseHostContext().portal !== null;
}

export function isPathPortalEnvironment() {
  return PATH_PORTALS_ENABLED;
}

export function getSubdomainBaseUrl(slug) {
  if (!slug) return null;
  if (PATH_PORTALS_ENABLED) {
    const protocol = window.location.protocol;
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${protocol}//${window.location.hostname}${port}/${encodeURIComponent(slug)}`;
  }
  return getPortalUrl(slug, 'company');
}

/** Single-level subdomains, or path-based portal URLs for isolated environments. */
export function defaultPortalHosts(slug) {
  const s = String(slug || '').toLowerCase();
  const domain = isDevHost() ? 'localhost' : (CONFIGURED_BASE_DOMAIN || getBaseDomain());

  if (PATH_PORTALS_ENABLED) {
    const encoded = encodeURIComponent(s);
    return {
      company: `${domain}/${encoded}/login`,
      rider: `${domain}/${encoded}/rider/login`,
      merchant: `${domain}/${encoded}/m/login`,
      customer: domain,
    };
  }

  return {
    company: `${s}${NAMESPACE_SUFFIX}.${domain}`,
    rider: `rider-${s}${NAMESPACE_SUFFIX}.${domain}`,
    merchant: `merchant-${s}${NAMESPACE_SUFFIX}.${domain}`,
    customer: `customer-${s}${NAMESPACE_SUFFIX}.${domain}`,
  };
}

export function defaultPortalHostsFlat(slug) {
  return defaultPortalHosts(slug);
}

export function getPortalUrl(slug, portal, customHosts = null) {
  if (!slug && portal !== 'admin') return null;

  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';

  if (customHosts) {
    const key = `domain_${portal}`;
    if (customHosts[key]) return `${protocol}//${customHosts[key]}${port}`;
  }

  if (PATH_PORTALS_ENABLED) {
    const origin = `${protocol}//${window.location.hostname}${port}`;
    if (portal === 'admin') return `${origin}/admin/login`;
    const encodedSlug = encodeURIComponent(slug);
    if (portal === 'company') return `${origin}/${encodedSlug}/login`;
    if (portal === 'merchant') return `${origin}/${encodedSlug}/m/login`;
    if (portal === 'rider') return `${origin}/${encodedSlug}/rider/login`;
    if (portal === 'customer') return `${origin}/track`;
    return origin;
  }

  const domain = isDevHost() ? 'localhost' : (CONFIGURED_BASE_DOMAIN || getBaseDomain());
  if (portal === 'admin') return `${protocol}//admin${NAMESPACE_SUFFIX}.${domain}${port}`;
  if (portal === 'company') return `${protocol}//${slug}${NAMESPACE_SUFFIX}.${domain}${port}`;
  return `${protocol}//${portal}-${slug}${NAMESPACE_SUFFIX}.${domain}${port}`;
}

export function getPortalUrlFlat(slug, portal, customHosts = null) {
  return getPortalUrl(slug, portal, customHosts);
}
